import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useState, useCallback, useEffect } from 'react'
import { useSessionStore, waitForHydration } from '@/stores/sessionStore'
import { useClerkLogin, useLogout } from '@/api/auth'
import { useRegisters } from '@/api/stores'
import { getElectronShell } from '@/lib/electronShell'
import { Leaf, Delete, ArrowRight, ChevronLeft, LogOut } from 'lucide-react'

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    await waitForHydration()
    const { backendUrl, sessionToken, selectedRegister } = useSessionStore.getState()
    if (!backendUrl) {
      throw redirect({ to: '/connect' })
    }
    if (!sessionToken) {
      throw redirect({ to: '/auth' })
    }
    if (!selectedRegister) {
      throw redirect({ to: '/select-register' })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { isAuthenticated, selectedRegister, selectedStore, backendUrl } = useSessionStore()
  const authType = useSessionStore((s) => s.authType)
  const accessibleStores = useSessionStore((s) => s.accessibleStores)
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)

  const clerkLoginMutation = useClerkLogin()
  const logoutMutation = useLogout()

  // Pulled from cache (/select-register populated it). When the store
  // only has one register there's nothing to "change register" to, so
  // the back button shifts to either "Change Store" (manager auth, who
  // can re-pick from accessibleStores) or "Logout" (store/API-key auth,
  // which is locked to one store).
  const { data: registers } = useRegisters(selectedStore?.id ?? null, selectedStore?.name)
  const onlyOneRegister = !!registers && registers.length === 1
  const canChangeStore =
    authType === 'manager' && !!accessibleStores && accessibleStores.length > 1
  // Electron handles auth at the shell level, so we hide the in-app
  // logout fallback. Change Register / Change Store still render.
  const isElectron = !!getElectronShell()
  const showsLogout = onlyOneRegister && !canChangeStore
  const hideBackButton = isElectron && showsLogout

  useEffect(() => {
    if (isAuthenticated && selectedRegister) {
      navigate({ to: '/pos' })
    }
  }, [isAuthenticated, selectedRegister, navigate])

  const handleKeyPress = useCallback((key: string) => {
    setError(null)
    if (key === 'backspace') {
      setPin(prev => prev.slice(0, -1))
    } else if (pin.length < 12) {
      setPin(prev => prev + key)
    }
  }, [pin.length])

  const handleSubmit = useCallback(async () => {
    if (pin.length < 3) {
      setError('PIN must be at least 3 digits')
      return
    }

    if (!selectedRegister) {
      setError('No register selected')
      return
    }

    setError(null)

    try {
      await clerkLoginMutation.mutateAsync({
        pin,
        register_id: selectedRegister.id,
      })
      navigate({ to: '/pos' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid PIN. Please try again.')
      setPin('')
    }
  }, [pin, clerkLoginMutation, navigate, selectedRegister])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handleKeyPress(e.key)
      } else if (e.key === 'Backspace') {
        handleKeyPress('backspace')
      } else if (e.key === 'Enter' && pin.length >= 3) {
        handleSubmit()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyPress, handleSubmit, pin.length])

  const numpadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace']

  const handleBack = () => {
    if (!onlyOneRegister) {
      navigate({ to: '/select-register' })
      return
    }
    // Single-register store: nothing to change to within the register
    // picker. Two outcomes:
    //   - Manager auth with multiple accessible stores → re-route to
    //     /select-store so they can pick a different store. Their
    //     session is still valid; no need to log out.
    //   - Store/API-key auth (or single-store manager) → full logout
    //     and back to /auth so a different account can sign in.
    if (canChangeStore) {
      navigate({ to: '/select-store' })
      return
    }
    logoutMutation.mutate('full', {
      onSettled: () => navigate({ to: '/auth' }),
    })
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-600 to-green-800 p-8">
      {/* Back button */}
      {!hideBackButton && (
        <button
          onClick={handleBack}
          disabled={logoutMutation.isPending}
          className="absolute top-6 left-6 flex items-center gap-1 text-white/80 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          {!onlyOneRegister ? (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Change Register</span>
            </>
          ) : canChangeStore ? (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Change Store</span>
            </>
          ) : (
            <>
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">Logout</span>
            </>
          )}
        </button>
      )}

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur rounded-2xl mb-4">
            <Leaf className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">WAIO POS</h1>
          <p className="text-green-100 mt-2">Enter your PIN to continue</p>
          {/* Show selected store/register */}
          <div className="mt-3 px-4 py-2 bg-white/10 backdrop-blur rounded-lg inline-block">
            <p className="text-white/90 text-sm">
              {selectedStore?.name} {selectedRegister && <>• {selectedRegister.name}</>}
            </p>
            <p className="text-white/60 text-xs mt-0.5">
              {backendUrl?.replace('https://', '').replace('http://', '')}
            </p>
          </div>
        </div>

        {/* PIN Display */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
          <div className="flex justify-center gap-2 mb-6 min-h-[20px]">
            {pin.length === 0 ? (
              <span className="text-gray-400 text-sm">Enter PIN</span>
            ) : (
              Array.from({ length: pin.length }).map((_, index) => (
                <div
                  key={index}
                  className="w-3.5 h-3.5 rounded-full bg-green-500 animate-scale-in"
                />
              ))
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="text-center text-red-500 text-sm mb-4 animate-fade-in">
              {error}
            </div>
          )}

          {/* Numpad. ph-no-capture: PostHog click-autocapture records
              button text ("Clicked button with text '4'"), which would
              make the PIN reconstructable from the replay event stream. */}
          <div className="grid grid-cols-3 gap-3 ph-no-capture">
            {numpadKeys.map(key => {
              if (key === 'clear') {
                return (
                  <button
                    key={key}
                    onClick={() => setPin('')}
                    className="h-16 rounded-xl text-gray-400 text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    Clear
                  </button>
                )
              }

              if (key === 'backspace') {
                return (
                  <button
                    key={key}
                    onClick={() => handleKeyPress('backspace')}
                    className="h-16 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <Delete className="w-6 h-6" />
                  </button>
                )
              }

              return (
                <button
                  key={key}
                  onClick={() => handleKeyPress(key)}
                  className="h-16 rounded-xl text-2xl font-semibold text-gray-800 hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                >
                  {key}
                </button>
              )
            })}
          </div>

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={pin.length < 3 || clerkLoginMutation.isPending}
            className="w-full mt-4 h-14 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer"
          >
            {clerkLoginMutation.isPending ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-green-100/60 text-sm">
          v2.0.0
        </p>
      </div>
    </div>
  )
}
